import React, { useEffect, useRef, useState, useMemo } from "react";
import { useInformationStyles } from "./InformationStyles";
import Titulo from "../UI/Titulo/Titulo";
import { DataInfoUtils, InfoAction, InfoItem, InfoColors } from "./data";
import InfoUtilsCard from "../../assets/images/InfoUtilsCard.svg";
import { InformationItem } from "./InformationItem";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { InfoActionRenderers } from "./InfoActions";
import { useContactoEmergencia } from "./hooks/useContactoEmergencia";
import NavigationModal from "./NavigationModal/NavigationModal";

export const Information = () => {
  const classes = useInformationStyles();
  const [open, setOpen] = useState(false);
  const [actions, setActions] = useState<InfoAction[]>([]);
  const [selectedItem, setSelectedItem] = useState<InfoItem | null>(null);
  const [navigationModal, setNavigationModal] = useState({
    open: false,
    path: "",
  });
  const popupRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const usuario = useSelector((state: RootState) => state.usuario.user);
  const modoMayor = usuario?.modoMayor;

  const contactoEmergenciaActions = useContactoEmergencia(usuario);

  const dynamicInfoUtils: InfoItem[] = useMemo(() => {
    return DataInfoUtils.map((item) => {
      if (item.id === 1) {
        // Contacto de Emergencia
        return {
          ...item,
          actions: contactoEmergenciaActions,
          description: "Estás por enviar una alerta de emergencia.",
        };
      }
      return item;
    });
  }, [contactoEmergenciaActions]);

  const closePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setOpen(false);
      setIsClosing(false);
    }, 200);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Verificar si hay un modal de emergencia abierto y cargando
      const emergencyModal = document.querySelector('[class*="modal"]');
      const isEmergencyLoading = document.querySelector(
        '[class*="modal"] [role="progressbar"]'
      );

      if (emergencyModal && isEmergencyLoading) return;

      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        closePopup();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleClick = (item: InfoItem) => {
    if (!open && item.actions?.length) {
      const navigationAction = item.actions.find(
        (action) => action.action === "navigate"
      );
      if (navigationAction) {
        setNavigationModal({
          open: true,
          path: navigationAction.path,
        });
        return;
      }

      setSelectedItem(item);
      setActions(item.actions);
      setOpen(true);
    }
  };

  const handleCloseNavigationModal = () => {
    setNavigationModal({ open: false, path: "" });
  };

  return (
    <section className={classes.root}>
      <Titulo>Información Útil</Titulo>
      <section className={classes.content}>
        <div
          className={`${classes.panel} ${
            modoMayor ? classes.modoMayorPanel : ""
          }`}
        >
          {dynamicInfoUtils.map((item, index) => (
            <InformationItem
              key={item.id}
              idx={index}
              item={{
                ...item,
                color: InfoColors[index % InfoColors.length],
              }}
              onClick={handleClick}
              modoMayor={modoMayor}
            />
          ))}
        </div>
      </section>
      {open && (
        <div
          className={classes.popup}
          ref={popupRef}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closePopup();
            }
          }}
        >
          <div
            className={`${classes.popupContent} ${
              isClosing ? classes.popupFadeOut : classes.popupFadeIn
            }`}
          >
            {selectedItem && (
              <div className={classes.modalHeader}>
                <div
                  className={classes.modalIconWrapper}
                  style={{ backgroundColor: selectedItem.color || "#E1A113" }}
                >
                  <img
                    className={classes.modalIcon}
                    src={selectedItem.icon}
                    alt={selectedItem.label}
                  />
                </div>
                <h3 className={classes.modalTitle}>
                  {selectedItem?.description || selectedItem.label}
                </h3>
              </div>
            )}
            <img
              src={InfoUtilsCard}
              alt="Información Útil"
              style={{ width: "100%", maxHeight: "150px" }}
            />
            <div className={classes.actionsContainer}>
              {actions.map((a, i) => (
                <React.Fragment key={i}>
                  {InfoActionRenderers[
                    a.action as keyof typeof InfoActionRenderers
                  ](a as any, i, classes)}
                </React.Fragment>
              ))}
              <button onClick={closePopup} className={classes.actionButton}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      <NavigationModal
        open={navigationModal.open}
        onClose={handleCloseNavigationModal}
        path={navigationModal.path}
      />
    </section>
  );
};
